// Code generated by tools/proto/generate.mjs. DO NOT EDIT.
//
// Source of truth: proto/ejot/roofing/v1/*.proto
// Regenerate with: make generate

// ── common.proto ────────────────────────────────────────────────────────

/**
 * Unit system in which a project's values are expressed. Only metric is
 * supported in the initial release; the enum exists so that adding an
 * imperial system later is not a breaking contract change.
 */
export type UnitSystem =
  | "UNIT_SYSTEM_UNSPECIFIED"
  | "UNIT_SYSTEM_METRIC";

export const UnitSystemValues: readonly UnitSystem[] = [
  "UNIT_SYSTEM_UNSPECIFIED",
  "UNIT_SYSTEM_METRIC",
] as const;

/** Severity of a validation, engineering or installation message. */
export type Severity =
  | "SEVERITY_UNSPECIFIED"
  | "SEVERITY_INFO"
  | "SEVERITY_WARNING"
  | "SEVERITY_ERROR";

export const SeverityValues: readonly Severity[] = [
  "SEVERITY_UNSPECIFIED",
  "SEVERITY_INFO",
  "SEVERITY_WARNING",
  "SEVERITY_ERROR",
] as const;

/**
 * Category of a message, used to route it to the correct panel and to decide
 * whether it appears in the generated report.
 */
export type MessageCategory =
  | "MESSAGE_CATEGORY_UNSPECIFIED"
  | "MESSAGE_CATEGORY_BLOCKING_INPUT"
  | "MESSAGE_CATEGORY_GEOMETRY"
  | "MESSAGE_CATEGORY_UNSUPPORTED_SCOPE"
  | "MESSAGE_CATEGORY_MISSING_PRODUCT_DATA"
  | "MESSAGE_CATEGORY_PRODUCT_INCOMPATIBILITY"
  | "MESSAGE_CATEGORY_ENGINEERING"
  | "MESSAGE_CATEGORY_INSTALLATION"
  | "MESSAGE_CATEGORY_DATASET_VERSION"
  | "MESSAGE_CATEGORY_REPORT"
  | "MESSAGE_CATEGORY_ASSUMPTION";

export const MessageCategoryValues: readonly MessageCategory[] = [
  "MESSAGE_CATEGORY_UNSPECIFIED",
  "MESSAGE_CATEGORY_BLOCKING_INPUT",
  "MESSAGE_CATEGORY_GEOMETRY",
  "MESSAGE_CATEGORY_UNSUPPORTED_SCOPE",
  "MESSAGE_CATEGORY_MISSING_PRODUCT_DATA",
  "MESSAGE_CATEGORY_PRODUCT_INCOMPATIBILITY",
  "MESSAGE_CATEGORY_ENGINEERING",
  "MESSAGE_CATEGORY_INSTALLATION",
  "MESSAGE_CATEGORY_DATASET_VERSION",
  "MESSAGE_CATEGORY_REPORT",
  "MESSAGE_CATEGORY_ASSUMPTION",
] as const;

/**
 * Outcome of a calculation. BLOCKED is distinct from FAIL: a blocked result
 * means the verification could not be completed, and must never be presented
 * as a passing result.
 */
export type CalculationStatus =
  | "CALCULATION_STATUS_UNSPECIFIED"
  | "CALCULATION_STATUS_PASS"
  | "CALCULATION_STATUS_FAIL"
  | "CALCULATION_STATUS_WARNING"
  | "CALCULATION_STATUS_BLOCKED"
  | "CALCULATION_STATUS_NOT_CALCULATED";

export const CalculationStatusValues: readonly CalculationStatus[] = [
  "CALCULATION_STATUS_UNSPECIFIED",
  "CALCULATION_STATUS_PASS",
  "CALCULATION_STATUS_FAIL",
  "CALCULATION_STATUS_WARNING",
  "CALCULATION_STATUS_BLOCKED",
  "CALCULATION_STATUS_NOT_CALCULATED",
] as const;

/**
 * Whether a value was supplied by the user or derived by the engine. Derived
 * values are presented read-only and visually distinct from editable input.
 */
export type ValueOrigin =
  | "VALUE_ORIGIN_UNSPECIFIED"
  | "VALUE_ORIGIN_INPUT"
  | "VALUE_ORIGIN_DERIVED"
  | "VALUE_ORIGIN_DATASET"
  | "VALUE_ORIGIN_MANUAL_OVERRIDE";

export const ValueOriginValues: readonly ValueOrigin[] = [
  "VALUE_ORIGIN_UNSPECIFIED",
  "VALUE_ORIGIN_INPUT",
  "VALUE_ORIGIN_DERIVED",
  "VALUE_ORIGIN_DATASET",
  "VALUE_ORIGIN_MANUAL_OVERRIDE",
] as const;

/**
 * A dimensionless numeric quantity carrying its presentation and provenance
 * metadata. Display precision is deliberately separate from the stored value:
 * calculations never round prematurely.
 */
export interface Quantity {
  value?: number;
  /**
   * Engineering symbol, for example "q_p". Rendered with correct subscript
   * formatting by the interface and the report.
   */
  symbol?: string;
  /** Unit label, for example "kN/m2". Empty for dimensionless values. */
  unit?: string;
  /** Number of decimal places to display. Does not affect the stored value. */
  display_precision?: number;
  origin?: ValueOrigin;
  /** Standard clause, dataset record or product record this value came from. */
  reference?: ReferenceInfo;
}

/** A length in millimetres. */
export interface Length {
  millimetres?: number;
  origin?: ValueOrigin;
}

/** An area in square metres. */
export interface Area {
  square_metres?: number;
  origin?: ValueOrigin;
}

/** A pressure in kilonewtons per square metre. */
export interface Pressure {
  kilonewtons_per_square_metre?: number;
  origin?: ValueOrigin;
}

/** A force in kilonewtons. */
export interface Force {
  kilonewtons?: number;
  origin?: ValueOrigin;
}

/**
 * An angle in degrees, measured counter-clockwise from the positive X axis
 * unless the containing message states otherwise.
 */
export interface Angle {
  degrees?: number;
  origin?: ValueOrigin;
}

/**
 * Reserved for a future localization effort. The initial release is English
 * only, so only the default text is populated.
 */
export interface LocalizedText {
  text?: string;
}

/**
 * Identifies the exact software and data versions that produced a result.
 * Returned with every calculation and recorded in every audit entry.
 */
export interface VersionInfo {
  application_version?: string;
  engine_version?: string;
  catalogue_version?: string;
  engineering_dataset_version?: string;
  project_schema_version?: string;
}

/**
 * A citation to the source of a value: a standard clause, a national dataset
 * record, a product record, or an approval document.
 */
export interface ReferenceInfo {
  /** Stable identifier of the referenced record or clause. */
  reference_id?: string;
  /** Human-readable citation shown in the interface and the report. */
  display_text?: string;
  /** Version of the dataset or document the reference points into. */
  source_version?: string;
}

/**
 * A validation, engineering or installation message. Every message carries a
 * stable code so that tests and support can refer to it without depending on
 * the wording.
 */
export interface ValidationMessage {
  /** Stable machine-readable code, for example "GEOM_SELF_INTERSECTION". */
  code?: string;
  severity?: Severity;
  category?: MessageCategory;
  /** Concise title shown in the messages panel. */
  title?: string;
  /** Full explanation of what is wrong and why it matters. */
  explanation?: string;
  /** What the user must do to resolve it. Empty for informational messages. */
  required_action?: string;
  /** Identifier of the input field or geometry object the message applies to. */
  affected_object_id?: string;
  /** Standard clause or documentation citation, where one applies. */
  reference?: ReferenceInfo;
  /** Whether this message must appear in the generated report. */
  include_in_report?: boolean;
}

/**
 * A recorded manual override of a derived value, with the justification and
 * the responsible user. Overrides are always auditable.
 */
export interface ManualOverride {
  field_id?: string;
  reason?: string;
  responsible_user?: string;
  recorded_at?: string;
}

// ── geometry.proto ──────────────────────────────────────────────────────

/**
 * Classification of a roof edge. Drives wind-zone generation and the
 * permitted membrane termination and perimeter-fixing treatment.
 */
export type RoofEdgeType =
  | "ROOF_EDGE_TYPE_UNSPECIFIED"
  | "ROOF_EDGE_TYPE_PARAPET"
  | "ROOF_EDGE_TYPE_FREE_EDGE"
  | "ROOF_EDGE_TYPE_ABUTMENT"
  | "ROOF_EDGE_TYPE_EXPANSION_JOINT";

export const RoofEdgeTypeValues: readonly RoofEdgeType[] = [
  "ROOF_EDGE_TYPE_UNSPECIFIED",
  "ROOF_EDGE_TYPE_PARAPET",
  "ROOF_EDGE_TYPE_FREE_EDGE",
  "ROOF_EDGE_TYPE_ABUTMENT",
  "ROOF_EDGE_TYPE_EXPANSION_JOINT",
] as const;

/** Classification of a roof opening. */
export type OpeningType =
  | "OPENING_TYPE_UNSPECIFIED"
  | "OPENING_TYPE_SKYLIGHT"
  | "OPENING_TYPE_SMOKE_VENT"
  | "OPENING_TYPE_SERVICE_PENETRATION"
  | "OPENING_TYPE_ROOF_ACCESS"
  | "OPENING_TYPE_GENERIC";

export const OpeningTypeValues: readonly OpeningType[] = [
  "OPENING_TYPE_UNSPECIFIED",
  "OPENING_TYPE_SKYLIGHT",
  "OPENING_TYPE_SMOKE_VENT",
  "OPENING_TYPE_SERVICE_PENETRATION",
  "OPENING_TYPE_ROOF_ACCESS",
  "OPENING_TYPE_GENERIC",
] as const;

/**
 * Wind-zone classification. The concrete rule set that produces these zones
 * depends on the approved design framework and is not yet fixed; the enum
 * captures the classification vocabulary the geometry and report layers need.
 * Flat-roof wind zone class. EN 1991-1-4 Table 7.2 distinguishes four zones,
 * and each one carries its own pressure coefficient and its own installation
 * settings, so INTERIOR must not stand for both H and I.
 */
export type WindZoneClass =
  | "WIND_ZONE_CLASS_UNSPECIFIED"
  | "WIND_ZONE_CLASS_INTERIOR"
  | "WIND_ZONE_CLASS_EDGE"
  | "WIND_ZONE_CLASS_CORNER"
  | "WIND_ZONE_CLASS_INNER_EDGE";

export const WindZoneClassValues: readonly WindZoneClass[] = [
  "WIND_ZONE_CLASS_UNSPECIFIED",
  "WIND_ZONE_CLASS_INTERIOR",
  "WIND_ZONE_CLASS_EDGE",
  "WIND_ZONE_CLASS_CORNER",
  "WIND_ZONE_CLASS_INNER_EDGE",
] as const;

/**
 * Substrate category. Determines which fastener families and which section
 * editor mode apply.
 */
export type SubstrateCategory =
  | "SUBSTRATE_CATEGORY_UNSPECIFIED"
  | "SUBSTRATE_CATEGORY_CONCRETE"
  | "SUBSTRATE_CATEGORY_STEEL_DECK"
  | "SUBSTRATE_CATEGORY_TIMBER"
  | "SUBSTRATE_CATEGORY_BOARD"
  | "SUBSTRATE_CATEGORY_CUSTOM";

export const SubstrateCategoryValues: readonly SubstrateCategory[] = [
  "SUBSTRATE_CATEGORY_UNSPECIFIED",
  "SUBSTRATE_CATEGORY_CONCRETE",
  "SUBSTRATE_CATEGORY_STEEL_DECK",
  "SUBSTRATE_CATEGORY_TIMBER",
  "SUBSTRATE_CATEGORY_BOARD",
  "SUBSTRATE_CATEGORY_CUSTOM",
] as const;

/**
 * Kind of dimension annotation produced from geometry. Dimensions are always
 * generated from the model, never typed as free graphic labels.
 */
export type DimensionKind =
  | "DIMENSION_KIND_UNSPECIFIED"
  | "DIMENSION_KIND_HORIZONTAL"
  | "DIMENSION_KIND_VERTICAL"
  | "DIMENSION_KIND_ALIGNED"
  | "DIMENSION_KIND_OVERALL"
  | "DIMENSION_KIND_ZONE_WIDTH"
  | "DIMENSION_KIND_OPENING"
  | "DIMENSION_KIND_OFFSET"
  | "DIMENSION_KIND_ANGULAR";

export const DimensionKindValues: readonly DimensionKind[] = [
  "DIMENSION_KIND_UNSPECIFIED",
  "DIMENSION_KIND_HORIZONTAL",
  "DIMENSION_KIND_VERTICAL",
  "DIMENSION_KIND_ALIGNED",
  "DIMENSION_KIND_OVERALL",
  "DIMENSION_KIND_ZONE_WIDTH",
  "DIMENSION_KIND_OPENING",
  "DIMENSION_KIND_OFFSET",
  "DIMENSION_KIND_ANGULAR",
] as const;

/**
 * A drawing primitive kind. The scene is renderer-agnostic: the interface
 * canvas and the PDF engine consume the same primitives.
 */
export type DrawingPrimitiveKind =
  | "DRAWING_PRIMITIVE_KIND_UNSPECIFIED"
  | "DRAWING_PRIMITIVE_KIND_LINE"
  | "DRAWING_PRIMITIVE_KIND_POLYLINE"
  | "DRAWING_PRIMITIVE_KIND_POLYGON"
  | "DRAWING_PRIMITIVE_KIND_RECTANGLE"
  | "DRAWING_PRIMITIVE_KIND_CIRCLE"
  | "DRAWING_PRIMITIVE_KIND_ARC"
  | "DRAWING_PRIMITIVE_KIND_HATCH"
  | "DRAWING_PRIMITIVE_KIND_ARROW"
  | "DRAWING_PRIMITIVE_KIND_DIMENSION"
  | "DRAWING_PRIMITIVE_KIND_LEADER"
  | "DRAWING_PRIMITIVE_KIND_TEXT"
  | "DRAWING_PRIMITIVE_KIND_SYMBOL"
  | "DRAWING_PRIMITIVE_KIND_LEGEND_SWATCH"
  | "DRAWING_PRIMITIVE_KIND_SCALE_INDICATOR"
  | "DRAWING_PRIMITIVE_KIND_NORTH_ARROW"
  | "DRAWING_PRIMITIVE_KIND_SECTION_MARKER"
  | "DRAWING_PRIMITIVE_KIND_BREAK_LINE"
  | "DRAWING_PRIMITIVE_KIND_IMAGE";

export const DrawingPrimitiveKindValues: readonly DrawingPrimitiveKind[] = [
  "DRAWING_PRIMITIVE_KIND_UNSPECIFIED",
  "DRAWING_PRIMITIVE_KIND_LINE",
  "DRAWING_PRIMITIVE_KIND_POLYLINE",
  "DRAWING_PRIMITIVE_KIND_POLYGON",
  "DRAWING_PRIMITIVE_KIND_RECTANGLE",
  "DRAWING_PRIMITIVE_KIND_CIRCLE",
  "DRAWING_PRIMITIVE_KIND_ARC",
  "DRAWING_PRIMITIVE_KIND_HATCH",
  "DRAWING_PRIMITIVE_KIND_ARROW",
  "DRAWING_PRIMITIVE_KIND_DIMENSION",
  "DRAWING_PRIMITIVE_KIND_LEADER",
  "DRAWING_PRIMITIVE_KIND_TEXT",
  "DRAWING_PRIMITIVE_KIND_SYMBOL",
  "DRAWING_PRIMITIVE_KIND_LEGEND_SWATCH",
  "DRAWING_PRIMITIVE_KIND_SCALE_INDICATOR",
  "DRAWING_PRIMITIVE_KIND_NORTH_ARROW",
  "DRAWING_PRIMITIVE_KIND_SECTION_MARKER",
  "DRAWING_PRIMITIVE_KIND_BREAK_LINE",
  "DRAWING_PRIMITIVE_KIND_IMAGE",
] as const;

/** A point in the project's plan coordinate system, in millimetres. */
export interface Point2D {
  /**
   * Stable identifier, preserved across edits so that constraints, dimensions
   * and messages can refer to a vertex reliably.
   */
  id?: string;
  x?: number;
  y?: number;
  /** Prevents the editor from moving this point. */
  locked?: boolean;
}

/** A directed segment between two points of a polygon. */
export interface Segment2D {
  id?: string;
  start_point_id?: string;
  end_point_id?: string;
}

/**
 * A closed polygon. Winding is normalized by the geometry service:
 * outer boundaries counter-clockwise, holes clockwise.
 */
export interface Polygon2D {
  points?: Point2D[];
  segments?: Segment2D[];
}

/** A roof opening or excluded area. */
export interface OpeningGeometry {
  id?: string;
  display_name?: string;
  opening_type?: OpeningType;
  outline?: Polygon2D;
  /** Whether the opening area is subtracted from the net roof area. */
  excluded_from_net_area?: boolean;
  /** Whether the opening's edges receive additional perimeter fixing. */
  perimeter_fixing_enabled?: boolean;
  perimeter_fixing_spacing?: Length;
  show_dimensions_in_report?: boolean;
  notes?: string;
}

/** Per-edge properties that influence zone generation and fixing layout. */
export interface RoofEdgeProperties {
  segment_id?: string;
  edge_type?: RoofEdgeType;
  /**
   * Excluded edges take no part in wind-zone generation, for example where a
   * roof abuts a taller adjacent structure.
   */
  excluded_from_zone_generation?: boolean;
  /** Height of the parapet or termination above the roof surface. */
  parapet_height?: Length;
  perimeter_fixing_enabled?: boolean;
  perimeter_fixing_spacing?: Length;
  /**
   * Overrides the generated zone width on this edge. Values that cannot be
   * realised on the geometry are rejected rather than silently clamped.
   */
  custom_zone_width?: Length;
  notes?: string;
  /**
   * Set when any value on this edge was changed manually from its derived
   * default, so the report can distinguish assumed from specified values.
   */
  override?: ManualOverride;
}

/** A dimension annotation derived from the geometry model. */
export interface DimensionAnnotation {
  id?: string;
  kind?: DimensionKind;
  /** Geometry objects this dimension measures. */
  measured_object_ids?: string[];
  /** Anchor points in plan coordinates, used by both renderers. */
  anchors?: Point2D[];
  value_millimetres?: number;
  /** Preformatted label. Renderers must not recompute the number from this. */
  label?: string;
  visible?: boolean;
}

/** A generated wind zone with the geometry and metadata both renderers need. */
export interface WindZoneGeometry {
  id?: string;
  zone_class?: WindZoneClass;
  /** Label shown on drawings and in result tables, shared by UI and report. */
  display_label?: string;
  /** Identifier of the zone-generation rule that produced this polygon. */
  source_rule_id?: string;
  outline?: Polygon2D;
  area?: Area;
  /** Roof edges this zone is associated with. */
  associated_segment_ids?: string[];
  /**
   * Semantic colour token, for example "zone-corner". Equal zone classes
   * always resolve to the same token in the interface and the report.
   */
  colour_token?: string;
  /** Point at which the zone label is placed on drawings. */
  label_anchor?: Point2D;
  dimensions?: DimensionAnnotation[];
}

/** The canonical roof-plan geometry of one roof module. */
export interface RoofPlanGeometry {
  /** Outer roof boundary. */
  outline?: Polygon2D;
  openings?: OpeningGeometry[];
  edge_properties?: RoofEdgeProperties[];
  /** North direction in degrees, measured clockwise from the positive Y axis. */
  north_orientation?: Angle;
  unit_system?: UnitSystem;
  /**
   * Incremented on every committed geometry change. Results computed against
   * an older revision are stale and must not be displayed as current.
   */
  revision?: number;
  /** Version of the editor that produced this geometry, for migration. */
  editor_version?: string;
  gross_area?: Area;
  net_area?: Area;
  perimeter?: Length;
  dimensions?: DimensionAnnotation[];
  /**
   * Zones generated by the backend from this geometry. The frontend may
   * render a preview, but this field is the authoritative result.
   */
  wind_zones?: WindZoneGeometry[];
  messages?: ValidationMessage[];
}

/** Parametric trapezoidal steel deck profile geometry. */
export interface SteelDeckProfileGeometry {
  sheet_thickness?: Length;
  /** Centre-to-centre distance between ribs. */
  rib_pitch?: Length;
  upper_flange_width?: Length;
  lower_flange_width?: Length;
  profile_depth?: Length;
  web_angle?: Angle;
  crest_radius?: Length;
  trough_radius?: Length;
  /** Deck span direction relative to the roof plan's X axis. */
  orientation?: Angle;
  /** Whether the fixing layout is constrained to one fastener per rib. */
  one_fastener_per_rib?: boolean;
  minimum_edge_distance?: Length;
}

/** One layer of the roof build-up, ordered from the substrate upwards. */
export interface BuildUpLayer {
  id?: string;
  display_name?: string;
  thickness?: Length;
  /** Hatch or fill token used by both the section editor and the report. */
  hatch_token?: string;
}

/** The roof build-up and substrate section of one roof module. */
export interface SectionProfile {
  substrate_category?: SubstrateCategory;
  substrate_thickness?: Length;
  deck_profile?: SteelDeckProfileGeometry;
  layers?: BuildUpLayer[];
  base_insulation_thickness?: Length;
  additional_insulation_thickness?: Length;
  existing_layer_thickness?: Length;
  total_insulation_thickness?: Length;
  /** Depth the fastener is anchored into the substrate. */
  anchorage_depth?: Length;
  /** Total required fastener length derived from the build-up. */
  total_fixing_length?: Length;
  messages?: ValidationMessage[];
}

/** The complete roof build-up for a module, combining plan and section. */
export interface RoofBuildUp {
  plan?: RoofPlanGeometry;
  section?: SectionProfile;
}

/**
 * One drawable element in world coordinates. Styling is expressed as semantic
 * tokens rather than concrete colours so that the interface theme and the
 * report theme resolve them independently while staying consistent.
 */
export interface DrawingPrimitive {
  id?: string;
  kind?: DrawingPrimitiveKind;
  /** Vertices in world millimetres, interpreted according to kind. */
  points?: Point2D[];
  /** Semantic stroke colour token, for example "zone-corner" or "dimension". */
  stroke_token?: string;
  /** Semantic fill colour or hatch token. Empty for unfilled primitives. */
  fill_token?: string;
  /** Line style token, for example "solid", "dashed", "hairline". */
  line_style_token?: string;
  /** Text content for text, dimension and leader primitives. */
  text?: string;
  /** Text role token, for example "dimension", "zone-label", "caption". */
  text_role_token?: string;
  /** Rotation applied to text, in degrees counter-clockwise. */
  text_rotation_degrees?: number;
  /** Layer this primitive belongs to, for visibility control. */
  layer_id?: string;
  /**
   * Draw order within the layer. Deterministic ordering is required for
   * reproducible report output.
   */
  draw_order?: number;
}

/** A named, toggleable group of drawing primitives. */
export interface DrawingLayer {
  id?: string;
  display_name?: string;
  visible_by_default?: boolean;
}

/**
 * A complete renderer-agnostic drawing, produced by the backend from
 * authoritative geometry and calculation data. The PDF renderer consumes this
 * directly and never reconstructs geometry from formatted interface strings.
 */
export interface DrawingScene {
  id?: string;
  title?: string;
  layers?: DrawingLayer[];
  primitives?: DrawingPrimitive[];
  /** Bounding box of the scene content in world millimetres. */
  bounds_min?: Point2D;
  bounds_max?: Point2D;
  /** Scene units per world millimetre, when a fixed drawing scale is required. */
  preferred_scale?: number;
}

// ── location.proto ──────────────────────────────────────────────────────

/**
 * How the wind zone was selected. Recorded so the report can state whether a
 * value came from a map selection, a list selection, or a manual entry.
 */
export type LocationSelectionMethod =
  | "LOCATION_SELECTION_METHOD_UNSPECIFIED"
  | "LOCATION_SELECTION_METHOD_MAP"
  | "LOCATION_SELECTION_METHOD_LIST"
  | "LOCATION_SELECTION_METHOD_MANUAL";

export const LocationSelectionMethodValues: readonly LocationSelectionMethod[] = [
  "LOCATION_SELECTION_METHOD_UNSPECIFIED",
  "LOCATION_SELECTION_METHOD_MAP",
  "LOCATION_SELECTION_METHOD_LIST",
  "LOCATION_SELECTION_METHOD_MANUAL",
] as const;

/**
 * Review lifecycle for one country-specific wind-data package. The states are
 * deliberately explicit so that "data exists" can never be confused with
 * "data is approved for automatic production use".
 */
export type NationalWindDatasetState =
  | "NATIONAL_WIND_DATASET_STATE_UNSPECIFIED"
  | "NATIONAL_WIND_DATASET_STATE_UNSUPPORTED"
  | "NATIONAL_WIND_DATASET_STATE_DATA_COLLECTED"
  | "NATIONAL_WIND_DATASET_STATE_ENGINEERING_REVIEW"
  | "NATIONAL_WIND_DATASET_STATE_GOLDEN_CASE_VERIFIED"
  | "NATIONAL_WIND_DATASET_STATE_APPROVED"
  | "NATIONAL_WIND_DATASET_STATE_ACTIVE"
  | "NATIONAL_WIND_DATASET_STATE_WITHDRAWN";

export const NationalWindDatasetStateValues: readonly NationalWindDatasetState[] = [
  "NATIONAL_WIND_DATASET_STATE_UNSPECIFIED",
  "NATIONAL_WIND_DATASET_STATE_UNSUPPORTED",
  "NATIONAL_WIND_DATASET_STATE_DATA_COLLECTED",
  "NATIONAL_WIND_DATASET_STATE_ENGINEERING_REVIEW",
  "NATIONAL_WIND_DATASET_STATE_GOLDEN_CASE_VERIFIED",
  "NATIONAL_WIND_DATASET_STATE_APPROVED",
  "NATIONAL_WIND_DATASET_STATE_ACTIVE",
  "NATIONAL_WIND_DATASET_STATE_WITHDRAWN",
] as const;

/**
 * Lookup mechanisms a national source package may support. A package advertises
 * only the modes its approved source and licence actually permit.
 */
export type NationalWindLookupMode =
  | "NATIONAL_WIND_LOOKUP_MODE_UNSPECIFIED"
  | "NATIONAL_WIND_LOOKUP_MODE_MANUAL"
  | "NATIONAL_WIND_LOOKUP_MODE_ZONE_LIST"
  | "NATIONAL_WIND_LOOKUP_MODE_COORDINATE"
  | "NATIONAL_WIND_LOOKUP_MODE_MAP";

export const NationalWindLookupModeValues: readonly NationalWindLookupMode[] = [
  "NATIONAL_WIND_LOOKUP_MODE_UNSPECIFIED",
  "NATIONAL_WIND_LOOKUP_MODE_MANUAL",
  "NATIONAL_WIND_LOOKUP_MODE_ZONE_LIST",
  "NATIONAL_WIND_LOOKUP_MODE_COORDINATE",
  "NATIONAL_WIND_LOOKUP_MODE_MAP",
] as const;

/**
 * A supported design framework and version. The concrete frameworks are a
 * stakeholder decision and are supplied by the backend at runtime rather than
 * being fixed in the contract.
 */
export interface DesignMethod {
  id?: string;
  display_name?: string;
  version?: string;
  /** Plain-language statement of what this method does and does not cover. */
  scope_description?: string;
  /** Identifiers of national datasets compatible with this method. */
  compatible_dataset_ids?: string[];
  /** Identifiers of safety-factor packages valid for this method. */
  compatible_safety_factor_package_ids?: string[];
}

/**
 * Provenance and approval metadata for one country-specific wind-data package.
 * This manifest may exist before any engineering values are activated.
 */
export interface NationalWindDatasetManifest {
  id?: string;
  country_code?: string;
  display_name?: string;
  standard?: string;
  national_annex?: string;
  edition?: string;
  version?: string;
  state?: NationalWindDatasetState;
  lookup_modes?: NationalWindLookupMode[];
  source_citation?: string;
  /**
   * Plain-language statement of the licence/provenance basis allowing the
   * application to carry and use this dataset. Empty means activation is
   * forbidden.
   */
  source_licence?: string;
  source_reference?: ReferenceInfo;
  valid_from?: string;
  valid_until?: string;
  automatic_lookup_permitted?: boolean;
  manual_entry_permitted?: boolean;
  blocking_reasons?: string[];
}

/**
 * A versioned national wind dataset. Engineering values stay separate from the
 * lifecycle manifest so source collection/review can exist without implying
 * that wind-zone values are ready for calculation.
 */
export interface NationalDataset {
  id?: string;
  country_code?: string;
  display_name?: string;
  version?: string;
  /** Source of the underlying data, recorded for licensing traceability. */
  source_citation?: string;
  valid_from?: string;
  valid_until?: string;
  wind_zones?: WindZoneEntry[];
  terrain_categories?: TerrainCategoryEntry[];
  manifest?: NationalWindDatasetManifest;
}

/** One selectable wind zone within a national dataset. */
export interface WindZoneEntry {
  id?: string;
  display_name?: string;
  /** Fundamental basic wind velocity for this zone, in metres per second. */
  base_wind_velocity?: Quantity;
  reference?: ReferenceInfo;
}

/** One terrain or exposure category within a national dataset. */
export interface TerrainCategoryEntry {
  id?: string;
  /** Short code, for example "II". */
  code?: string;
  display_name?: string;
  /** Engineering description shown next to the selector. */
  description?: string;
  /**
   * Identifier of the generic technical illustration for this category.
   * External reference photographs are never used.
   */
  illustration_token?: string;
  reference?: ReferenceInfo;
}

/** A neighbouring building whose presence affects the design wind action. */
export interface NearbyBuilding {
  id?: string;
  height?: Length;
  average_height?: Length;
  length?: Length;
  /** Distance from the design building. */
  distance?: Length;
  /** Direction to the neighbouring building, measured like north orientation. */
  direction?: Angle;
}

/**
 * A safety-factor package. Values are supplied by an approved, versioned
 * engineering dataset and are never authored in application code.
 */
export interface SafetyFactorPackage {
  id?: string;
  display_name?: string;
  version?: string;
  reference?: ReferenceInfo;
  /** Named factors keyed by a stable factor identifier. */
  factors?: Record<string, Quantity>;
}

/**
 * Audit context for a wind velocity supplied manually. The numeric quantity is
 * repeated here intentionally: the source context and the value must travel
 * together so a manual value cannot later appear to have come from an automatic
 * national lookup.
 */
export interface ManualWindVelocityInput {
  value?: Quantity;
  source_citation?: string;
  source_reference?: ReferenceInfo;
  country_code?: string;
  national_annex?: string;
  entered_by?: string;
  entered_at?: string;
}

/** The complete location and environmental input of a project. */
export interface LocationInput {
  country_code?: string;
  region_id?: string;
  national_dataset_id?: string;
  wind_zone_id?: string;
  selection_method?: LocationSelectionMethod;
  elevation_above_sea_level?: Length;
  terrain_category_id?: string;
  /**
   * Legacy quantity field retained for project compatibility. New manual-mode
   * projects also populate manual_wind_velocity so the source is auditable.
   */
  base_wind_velocity_override?: Quantity;
  directional_factor?: Quantity;
  seasonal_factor?: Quantity;
  air_density?: Quantity;
  nearby_buildings_enabled?: boolean;
  nearby_buildings?: NearbyBuilding[];
  /**
   * North direction in degrees, clockwise from the positive Y axis. Roof
   * modules inherit this unless they define their own.
   */
  north_orientation?: Angle;
  overrides?: ManualOverride[];
  manual_wind_velocity?: ManualWindVelocityInput;
}

/**
 * Derived location values returned by the backend. All are read-only in the
 * interface and visually distinguished from editable input.
 */
export interface LocationResult {
  base_wind_velocity?: Quantity;
  basic_velocity_pressure?: Quantity;
  /** Plain-language statement of how the values above were arrived at. */
  calculation_basis?: string;
  dataset_reference?: ReferenceInfo;
  messages?: ValidationMessage[];
}

// ── product.proto ───────────────────────────────────────────────────────

/**
 * Approval state of a catalogue. Development catalogues contain synthetic
 * values and must never be usable for a real design.
 */
export type CatalogueApprovalState =
  | "CATALOGUE_APPROVAL_STATE_UNSPECIFIED"
  | "CATALOGUE_APPROVAL_STATE_DEVELOPMENT_ONLY"
  | "CATALOGUE_APPROVAL_STATE_APPROVED"
  | "CATALOGUE_APPROVAL_STATE_WITHDRAWN";

export const CatalogueApprovalStateValues: readonly CatalogueApprovalState[] = [
  "CATALOGUE_APPROVAL_STATE_UNSPECIFIED",
  "CATALOGUE_APPROVAL_STATE_DEVELOPMENT_ONLY",
  "CATALOGUE_APPROVAL_STATE_APPROVED",
  "CATALOGUE_APPROVAL_STATE_WITHDRAWN",
] as const;

/**
 * The resistance basis selected for a roof module. Complete-system design and
 * ETA component characteristic calculations are deliberately separate so a
 * component source value can never be presented as membrane-system capacity.
 */
export type FixingResistanceBasis =
  | "FIXING_RESISTANCE_BASIS_UNSPECIFIED"
  | "FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN"
  | "FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC";

export const FixingResistanceBasisValues: readonly FixingResistanceBasis[] = [
  "FIXING_RESISTANCE_BASIS_UNSPECIFIED",
  "FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN",
  "FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC",
] as const;

/** The physical mechanism a resistance value describes. */
export type ResistanceMode =
  | "RESISTANCE_MODE_UNSPECIFIED"
  | "RESISTANCE_MODE_MEMBRANE_PULL_THROUGH"
  | "RESISTANCE_MODE_WASHER_PULL_OVER"
  | "RESISTANCE_MODE_FASTENER_PULL_OUT"
  | "RESISTANCE_MODE_FASTENER_TENSILE"
  | "RESISTANCE_MODE_DYNAMIC"
  | "RESISTANCE_MODE_SYSTEM_MINIMUM"
  | "RESISTANCE_MODE_ETA_COMPONENT_PULL_THROUGH";

export const ResistanceModeValues: readonly ResistanceMode[] = [
  "RESISTANCE_MODE_UNSPECIFIED",
  "RESISTANCE_MODE_MEMBRANE_PULL_THROUGH",
  "RESISTANCE_MODE_WASHER_PULL_OVER",
  "RESISTANCE_MODE_FASTENER_PULL_OUT",
  "RESISTANCE_MODE_FASTENER_TENSILE",
  "RESISTANCE_MODE_DYNAMIC",
  "RESISTANCE_MODE_SYSTEM_MINIMUM",
  "RESISTANCE_MODE_ETA_COMPONENT_PULL_THROUGH",
] as const;

/**
 * A versioned catalogue release. Every calculation records the exact
 * catalogue version it used.
 */
export interface CatalogueVersion {
  id?: string;
  display_name?: string;
  version?: string;
  approval_state?: CatalogueApprovalState;
  released_at?: string;
  valid_from?: string;
  valid_until?: string;
  /** active, for example that results are not approved for real design. */
  usage_notice?: string;
}

/**
 * A characteristic resistance dataset for one mode, referencing the approval
 * document it derives from. Values are never authored in application code.
 */
export interface ResistanceDataset {
  id?: string;
  mode?: ResistanceMode;
  characteristic_resistance?: Quantity;
  /** Partial factor identifier applied to reach the design resistance. */
  safety_factor_id?: string;
  approval_reference?: ReferenceInfo;
  valid_from?: string;
  valid_until?: string;
  /** Set on synthetic development values so no approval is implied. */
  development_only?: boolean;
}

/** A waterproofing membrane product. */
export interface MembraneProduct {
  id?: string;
  display_name?: string;
  /** Reserved for approved catalogue data. Empty for generic products. */
  manufacturer?: string;
  membrane_family?: string;
  nominal_roll_width?: Length;
  usable_cover_width?: Length;
  default_overlap_width?: Length;
  thickness?: Length;
  /** Pull-through resistance datasets applicable to this membrane. */
  resistance_dataset_ids?: string[];
  minimum_fixing_density?: Quantity;
  minimum_spacing?: Length;
  maximum_spacing?: Length;
  approval_reference?: ReferenceInfo;
  valid_from?: string;
  valid_until?: string;
  /** Description and installation notes reproduced in the report. */
  report_description?: string;
  development_only?: boolean;
}

/** A screw or comparable fastener. */
export interface FastenerProduct {
  id?: string;
  display_name?: string;
  product_code?: string;
  diameter?: Length;
  available_length_minimum?: Length;
  available_length_maximum?: Length;
  /** Substrate categories this fastener is approved for. */
  compatible_substrates?: SubstrateCategory[];
  required_anchorage_depth?: Length;
  resistance_dataset_ids?: string[];
  approval_reference?: ReferenceInfo;
  development_only?: boolean;
}

/** A telescope, sleeve, washer or load-distribution plate. */
export interface TelescopeProduct {
  id?: string;
  display_name?: string;
  product_code?: string;
  plate_diameter?: Length;
  /** Insulation thickness range this component is designed for. */
  insulation_thickness_minimum?: Length;
  insulation_thickness_maximum?: Length;
  resistance_dataset_ids?: string[];
  approval_reference?: ReferenceInfo;
  development_only?: boolean;
}

/** A generic or approved substrate. */
export interface SubstrateProduct {
  id?: string;
  display_name?: string;
  category?: SubstrateCategory;
  minimum_thickness?: Length;
  /** Compressive or comparable grade designation, where it applies. */
  grade_designation?: string;
  development_only?: boolean;
}

/** A named steel deck profile with its parametric geometry. */
export interface SteelDeckProfile {
  id?: string;
  display_name?: string;
  /** Reserved for approved catalogue data. Empty for generic profiles. */
  manufacturer?: string;
  geometry?: SteelDeckProfileGeometry;
  development_only?: boolean;
}

/**
 * A compatible assembly of telescope, fastener, substrate and membrane. The
 * fixing combination is what the user ultimately selects and what quantities
 * are reported against.
 */
export interface FixingCombination {
  id?: string;
  display_name?: string;
  telescope_product_id?: string;
  fastener_product_id?: string;
  compatible_substrates?: SubstrateCategory[];
  /**
   * Membranes this combination is approved with. Empty means unrestricted
   * within the substrate constraint.
   */
  compatible_membrane_ids?: string[];
  insulation_thickness_minimum?: Length;
  insulation_thickness_maximum?: Length;
  total_length?: Length;
  anchorage_depth?: Length;
  /** Resistance datasets governing this combination, one per applicable mode. */
  resistance_dataset_ids?: string[];
  safety_factor_package_id?: string;
  /** Installation tools required, referencing accessory products. */
  required_accessory_ids?: string[];
  approval_reference?: ReferenceInfo;
  valid_from?: string;
  valid_until?: string;
  development_only?: boolean;
}

/** An installation tool, bit, adapter or other accessory. */
export interface AccessoryProduct {
  id?: string;
  display_name?: string;
  product_code?: string;
  /** Accessory category, for example "bit", "drill", "adapter", "extension". */
  category?: string;
  report_description?: string;
  development_only?: boolean;
}

/**
 * A rule that permits or forbids a combination of selections. Rules are data,
 * not code, so that catalogue releases can change them without a deployment.
 */
export interface CompatibilityRule {
  id?: string;
  /** Stable rule kind, for example "SUBSTRATE_FASTENER", "MEMBRANE_TELESCOPE". */
  rule_kind?: string;
  /** Identifiers the rule applies to, interpreted according to rule_kind. */
  subject_ids?: string[];
  /** When false, the combination is blocked and the message explains why. */
  permitted?: boolean;
  message?: ValidationMessage;
}

/** A source citation attached to one ETA reference record. */
export interface ETAReferenceRecordSource {
  page?: number;
  annex?: string;
}

/**
 * Identity of the exact ETA document from which the reference package was
 * extracted.
 */
export interface ETAReferenceDocument {
  eta_number?: string;
  issue_date?: string;
  pages?: number;
  annex_count?: number;
  source_sha256?: string;
}

/**
 * A real ETA product-family record. It is exposed for source traceability and
 * product browsing, not as a complete roofing-system resistance record.
 */
export interface ETAReferenceProduct {
  product_id?: string;
  eta_product_code?: string;
  product_type?: string;
  annex_group?: number;
  nominal_diameter_mm?: number;
  source?: ETAReferenceRecordSource;
}

/** A real ETA washer, plate or other component record. */
export interface ETAReferenceComponent {
  component_id?: string;
  component_code?: string;
  component_type?: string;
  source?: ETAReferenceRecordSource;
}

/** A substrate record preserved from the ETA source package. */
export interface ETAReferenceSubstrate {
  substrate_id?: string;
  display_name?: string;
  category?: string;
}

/** An exact product/component combination appearing in the ETA source package. */
export interface ETAReferenceCombination {
  combination_id?: string;
  eta_combination_code?: string;
  annex_group?: number;
  product_id?: string;
  component_id?: string;
  source?: ETAReferenceRecordSource;
}

/** value. No design conversion is performed by this reference catalogue. */
export interface ETAReferencePullOutResistance {
  pull_out_id?: string;
  product_id?: string;
  substrate_id?: string;
  condition_type?: string;
  condition_comparator?: string;
  condition_value?: Quantity;
  characteristic_resistance?: Quantity;
  source?: ETAReferenceRecordSource;
}

/** Characteristic pull-through resistance for one exact ETA combination. */
export interface ETAReferencePullThroughResistance {
  pull_through_id?: string;
  combination_id?: string;
  product_id?: string;
  component_id?: string;
  characteristic_resistance?: Quantity;
  source?: ETAReferenceRecordSource;
}

/** hidden at import time. */
export interface ETAReferenceQualityFlag {
  quality_flag_id?: string;
  severity?: string;
  entity_type?: string;
  entity_id?: string;
  issue?: string;
  source_page?: number;
  source_annex?: string;
  resolution_status?: string;
}

/**
 * Read-only runtime view of the validated ETA-07/0013 component package.
 * Complete waterproofing-system calculation use remains forbidden. The scoped
 * component characteristic flag only permits the separate ETA component basis
 * and never implies a design value or complete-system approval.
 */
export interface ETAReferenceCatalogue {
  dataset_id?: string;
  activation_state?: string;
  calculation_use_permitted?: boolean;
  blocking_reasons?: string[];
  source_document?: ETAReferenceDocument;
  products?: ETAReferenceProduct[];
  components?: ETAReferenceComponent[];
  substrates?: ETAReferenceSubstrate[];
  combinations?: ETAReferenceCombination[];
  pull_out_resistances?: ETAReferencePullOutResistance[];
  pull_through_resistances?: ETAReferencePullThroughResistance[];
  quality_flags?: ETAReferenceQualityFlag[];
  component_characteristic_calculation_permitted?: boolean;
}

/**
 * ETA records pinned to a project. The selected resistance rows are exact
 * source records; the backend never guesses a conditional pull-out row or
 * interpolates between records.
 */
export interface ETAReferenceSelection {
  dataset_id?: string;
  product_id?: string;
  component_id?: string;
  combination_id?: string;
  substrate_id?: string;
  pull_out_id?: string;
  pull_through_id?: string;
}

// ── project.proto ───────────────────────────────────────────────────────

/** How internal pressure is determined for a roof module. */
export type InternalPressureMethod =
  | "INTERNAL_PRESSURE_METHOD_UNSPECIFIED"
  | "INTERNAL_PRESSURE_METHOD_AUTOMATIC"
  | "INTERNAL_PRESSURE_METHOD_MANUAL";

export const InternalPressureMethodValues: readonly InternalPressureMethod[] = [
  "INTERNAL_PRESSURE_METHOD_UNSPECIFIED",
  "INTERNAL_PRESSURE_METHOD_AUTOMATIC",
  "INTERNAL_PRESSURE_METHOD_MANUAL",
] as const;

/** Organization or customer metadata reproduced on the report cover. */
export interface OrganizationMetadata {
  company_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  postcode?: string;
  city?: string;
  country?: string;
}

/** The person responsible for the design, and optionally the checker. */
export interface DesignerMetadata {
  name?: string;
  email?: string;
  phone?: string;
}

/** Descriptive project metadata. */
export interface ProjectMetadata {
  project_name?: string;
  subject?: string;
  address_line_1?: string;
  address_line_2?: string;
  postcode?: string;
  city?: string;
  country?: string;
  notes?: string;
  organization?: OrganizationMetadata;
  designer?: DesignerMetadata;
  checker?: DesignerMetadata;
}

/**
 * Which interface layers are visible. Stored per user, not per project, but
 * carried in the project file so a reopened project looks familiar.
 */
export interface ViewPreferences {
  /** Layer identifier to visibility. Unlisted layers use their default. */
  layer_visibility?: Record<string, boolean>;
  /** Theme identifier, "light" or "dark". */
  theme?: string;
  left_panel_width?: number;
  right_panel_width?: number;
}

/** Report generation preferences saved with the project. */
export interface ReportPreferences {
  /** Decimal separator character, "." or ",". */
  decimal_separator?: string;
  include_drawings?: boolean;
  /** Section identifiers selected for inclusion. */
  included_section_ids?: string[];
  show_page_numbers?: boolean;
  additional_notes?: string;
}

/** Internal-pressure configuration for one roof module. */
export interface InternalPressureSettings {
  method?: InternalPressureMethod;
  /** Identifier of the standard clause or method variant selected. */
  method_variant_id?: string;
  /** Populated only when method is MANUAL. */
  manual_coefficient_positive?: Quantity;
  manual_coefficient_negative?: Quantity;
  override?: ManualOverride;
}

/** Per-zone membrane and fixing layout settings. */
export interface ZoneLayoutSettings {
  /** Wind zone this configuration applies to. */
  zone_id?: string;
  membrane_width?: Length;
  overlap_width?: Length;
  maximum_spacing?: Length;
  /** Additional fixings placed outside the membrane overlap. */
  additional_fixings_outside_overlap?: boolean;
  additional_fixing_spacing?: Length;
  /** Identifier of the selected installation pattern, shared with the report. */
  pattern_id?: string;
  /** Membrane laying direction in degrees, relative to the plan X axis. */
  installation_direction?: Angle;
  fixed_telescope?: boolean;
  /**
   * Longitudinal offset applied cumulatively to successive membrane courses so
   * that the end laps between rolls form a running bond instead of lining up
   * in a continuous joint across the roof. Zero lays every course from the
   * same chainage origin.
   */
  course_start_stagger?: Length;
  /**
   * Commercial length of one membrane roll. A course longer than this is laid
   * as several rolls joined by end laps.
   */
  roll_length?: Length;
  /** Width of the transverse lap where two rolls meet along one course. */
  end_lap_width?: Length;
  /**
   * Whether a fixing row is added along the free outer edge of the first and
   * last horizontal course, where that edge is the roof's own outline rather
   * than a seam shared with an earlier-laid region.
   */
  perimeter_fixings_on_free_edge?: boolean;
}

/** The selected fixing resistance path and products for a roof module. */
export interface FixingSystemSelection {
  membrane_product_id?: string;
  fixing_combination_id?: string;
  substrate_product_id?: string;
  steel_deck_profile_id?: string;
  /**
   * Populated when the user defines a custom generic membrane rather than
   * selecting a catalogue record.
   */
  custom_membrane?: MembraneProduct;
  /** Populated when the user defines a custom generic deck profile. */
  custom_deck_profile?: SteelDeckProfile;
  /**
   * Exact ETA component/source selection. In ETA component characteristic
   * basis this replaces the complete membrane-system resistance requirement,
   * but it never becomes complete-system design resistance.
   */
  eta_reference?: ETAReferenceSelection;
  /**
   * component characteristic basis. UNSPECIFIED retains legacy complete-system
   * behavior for older stored projects.
   */
  resistance_basis?: FixingResistanceBasis;
}

/**
 * Insulation pre-fixing configuration, calculated as a separate quantity
 * stream from the membrane fixing.
 */
export interface InsulationFixingSettings {
  enabled?: boolean;
  density?: Quantity;
  fixing_combination_id?: string;
}

/** One roof module within a project. A project may contain several. */
export interface RoofModule {
  id?: string;
  display_name?: string;
  comment?: string;
  /**
   * Number of identical roofs this module represents. Applied to quantities
   * only after the per-module result is established.
   */
  quantity_multiplier?: number;
  roof_height_above_ground?: Length;
  roof_slope?: Angle;
  /**
   * Whether this module uses its own north orientation instead of the
   * project-level value.
   */
  override_north_orientation?: boolean;
  north_orientation?: Angle;
  build_up?: RoofBuildUp;
  internal_pressure?: InternalPressureSettings;
  fixing_system?: FixingSystemSelection;
  zone_layouts?: ZoneLayoutSettings[];
  insulation_fixing?: InsulationFixingSettings;
  /** Whether per-edge custom zone widths are permitted on this module. */
  allow_custom_zone_widths?: boolean;
}

/**
 * A complete project. The canonical project file is this message serialized
 * as Proto-JSON.
 */
export interface Project {
  id?: string;
  /**
   * Incremented when the persisted shape of this message changes, so that
   * older stored projects can be migrated deterministically.
   */
  schema_version?: string;
  /** Version of the application that last wrote this project. */
  application_version?: string;
  metadata?: ProjectMetadata;
  unit_system?: UnitSystem;
  /** Selected design framework and its version. */
  design_method_id?: string;
  safety_factor_package_id?: string;
  location?: LocationInput;
  modules?: RoofModule[];
  /** Catalogue and engineering dataset versions this project is pinned to. */
  catalogue_version_id?: string;
  engineering_dataset_version_id?: string;
  view_preferences?: ViewPreferences;
  report_preferences?: ReportPreferences;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  archived?: boolean;
}

/** Summary fields used for project listing without loading the full project. */
export interface ProjectSummary {
  id?: string;
  project_name?: string;
  subject?: string;
  city?: string;
  updated_at?: string;
  module_count?: number;
  last_calculation_status?: CalculationStatus;
  archived?: boolean;
}

// ── calculation.proto ───────────────────────────────────────────────────

/**
 * One entry in the calculation trace. Every reported value can be followed
 * back through these entries to its inputs and its governing reference.
 */
export interface CalculationTraceEntry {
  id?: string;
  /** Identifier of the formula or rule that produced the result. */
  rule_id?: string;
  /** Human-readable description of what this step computed. */
  description?: string;
  /** Identifiers of the input fields and prior trace entries consumed. */
  input_ids?: string[];
  /** Named intermediate values, keyed by symbol. */
  intermediates?: Record<string, Quantity>;
  result?: Quantity;
  reference?: ReferenceInfo;
}

/** The full ordered trace of a calculation. */
export interface CalculationTrace {
  entries?: CalculationTraceEntry[];
  engine_version?: string;
}

/**
 * A stated assumption that the result depends on. Assumptions always appear
 * in the report so the reader knows what was taken for granted.
 */
export interface Assumption {
  code?: string;
  title?: string;
  explanation?: string;
  reference?: ReferenceInfo;
}

/**
 * The wind input values a calculation was performed with, after dataset
 * resolution and unit normalization.
 */
export interface WindInput {
  design_method_id?: string;
  national_dataset_id?: string;
  wind_zone_id?: string;
  terrain_category_id?: string;
  base_wind_velocity?: Quantity;
  directional_factor?: Quantity;
  seasonal_factor?: Quantity;
  air_density?: Quantity;
  elevation?: Length;
  roof_height?: Length;
  roof_slope?: Angle;
}

/**
 * The derived wind parameters common to the whole module, before zone-level
 * pressure coefficients are applied.
 */
export interface WindResult {
  basic_wind_velocity?: Quantity;
  basic_velocity_pressure?: Quantity;
  exposure_factor?: Quantity;
  peak_velocity_pressure?: Quantity;
  /** Internal pressure coefficients evaluated for the mandatory cases. */
  internal_pressure_coefficients?: Quantity[];
  /**
   * The internal-pressure case that governs, after all mandatory cases were
   * evaluated. The interface must not hide an adverse governing case.
   */
  governing_internal_pressure?: Quantity;
  load_safety_factor?: Quantity;
  messages?: ValidationMessage[];
}

/** The evaluation of one resistance mode for the selected fixing system. */
export interface ResistanceResult {
  mode?: ResistanceMode;
  /**
   * Whether this mode was actually evaluated. A mode that could not be
   * evaluated never contributes to a passing result.
   */
  evaluated?: boolean;
  characteristic_resistance?: Quantity;
  partial_factor?: Quantity;
  design_resistance?: Quantity;
  /** Identifier of the resistance dataset/source record used. */
  resistance_dataset_id?: string;
  reference?: ReferenceInfo;
  /**
   * Set on the mode that produced the governing minimum resistance for the
   * active basis.
   */
  governing?: boolean;
  messages?: ValidationMessage[];
}

/**
 * The fixing requirement for one zone. Complete-system design basis may also
 * populate membrane overlap/spacing fields. ETA component characteristic basis
 * intentionally leaves membrane-specific layout fields empty.
 */
export interface FixingLayoutResult {
  zone_id?: string;
  /** Requirement derived from the active wind/resistance basis. */
  theoretical_density?: Quantity;
  /**
   * Requirement after membrane width, overlap, spacing limits, deck rib
   * pitch, minimum density and discrete rounding are applied.
   */
  installable_density?: Quantity;
  /**
   * Ordered explanations of why the installable density differs from the
   * theoretical density. Reproduced in the report.
   */
  adjustment_reasons?: string[];
  spacing_in_overlap?: Length;
  fixings_per_metre?: Quantity;
  membrane_width?: Length;
  overlap_width?: Length;
  pattern_id?: string;
  fixings_in_overlap?: number;
  additional_fixings?: number;
  total_fixings?: number;
  /**
   * Membrane course settings this layout was laid with, echoed so that the
   * installation table and the laying plan can be checked against each other.
   */
  course_start_stagger?: Length;
  roll_length?: Length;
  end_lap_width?: Length;
  /**
   * Number of membrane courses that cross this zone, counted on the roof-wide
   * course lattice rather than on the zone's own bounding box.
   */
  course_count?: number;
  /**
   * Fixings added along the free outer edge of the first/last course, where
   * that edge is the roof's own outline rather than a seam shared with an
   * earlier-laid region. Zero unless perimeter_fixings_on_free_edge is set.
   */
  perimeter_fixings?: number;
}

/** The complete result for one wind zone. */
export interface ZoneResult {
  zone_id?: string;
  zone_class?: WindZoneClass;
  display_label?: string;
  area?: Area;
  external_pressure_coefficient?: Quantity;
  characteristic_pressure?: Quantity;
  design_pressure?: Quantity;
  resistances?: ResistanceResult[];
  governing_design_resistance?: Quantity;
  layout?: FixingLayoutResult;
  /**
   * Design action divided by design resistance, as a fraction. This remains
   * empty in ETA component characteristic basis because no design resistance
   * or installation-layout verification is claimed.
   */
  utilization?: Quantity;
  status?: CalculationStatus;
  messages?: ValidationMessage[];
  /**
   * Populated for ETA component characteristic basis; never relabelled as a
   * design resistance.
   */
  governing_characteristic_resistance?: Quantity;
  resistance_basis?: FixingResistanceBasis;
}

/** A quantity line in a bill of materials. */
export interface QuantityLine {
  /** Product or component this line counts. */
  product_id?: string;
  display_name?: string;
  /** Grouping key, for example a fixing length or insulation thickness. */
  group_key?: string;
  quantity?: number;
  unit?: string;
  development_only?: boolean;
}

/** Aggregated quantities at module or project level. */
export interface QuantitySummary {
  lines?: QuantityLine[];
  total_membrane_area?: Area;
  estimated_membrane_rolls?: number;
  total_fixings?: number;
  total_insulation_fixings?: number;
  total_perimeter_fixings?: number;
  /**
   * Waste allowance, reported separately and never folded into the
   * engineering quantity.
   */
  waste_factor_percent?: number;
}

/**
 * Membrane material takeoff for one wind/roof sub-zone within a module's
 * generated laying plan. Every value is summed from the actual membrane
 * scene, so it already includes the material consumed by overlaps; it is
 * never derived from the zone's own polygon area.
 */
export interface MembraneZoneTakeoff {
  zone_id?: string;
  display_label?: string;
  /**
   * Actual roll widths used by pieces generated for this zone, in the order
   * first encountered. Almost always one value; more than one only if this
   * zone's own layout legitimately mixes roll widths, in which case they
   * must never be averaged into a fictitious width.
   */
  membrane_widths?: Length[];
  total_length?: Length;
  total_area?: Area;
  piece_count?: number;
}

/**
 * Membrane material takeoff for one module's generated laying plan, summed
 * from the actual generated membrane pieces across every zone. This is the
 * authoritative procurement quantity: it is larger than the net roof area
 * whenever overlaps are present, and that is correct.
 */
export interface MembraneTakeoff {
  zones?: MembraneZoneTakeoff[];
  total_length?: Length;
  total_area?: Area;
}

/** The result for one roof module. */
export interface ModuleResult {
  module_id?: string;
  status?: CalculationStatus;
  wind_input?: WindInput;
  wind?: WindResult;
  zones?: ZoneResult[];
  /**
   * Identifier of the zone governing the active basis. In ETA component
   * characteristic mode this is the zone with the highest required density.
   */
  governing_zone_id?: string;
  governing_resistance_mode?: ResistanceMode;
  highest_utilization?: Quantity;
  quantities?: QuantitySummary;
  geometry?: RoofPlanGeometry;
  drawing_scenes?: DrawingScene[];
  trace?: CalculationTrace;
  assumptions?: Assumption[];
  messages?: ValidationMessage[];
  resistance_basis?: FixingResistanceBasis;
  /**
   * Membrane procurement takeoff for this module's generated laying plan,
   * summed from the actual membrane pieces in the membrane-layout scene.
   */
  membrane_takeoff?: MembraneTakeoff;
}

/**
 * A request to calculate one module or a whole project. The complete project
 * is sent so that the backend never depends on interface-held state.
 */
export interface CalculationRequest {
  project?: Project;
  /** When set, only this module is calculated. Empty calculates every module. */
  module_id?: string;
  /** Opaque token echoed in the response, used to discard superseded results. */
  request_token?: string;
  /** When true the result is persisted as an immutable audit record. */
  persist_audit?: boolean;
}

/**
 * The result of a calculation. This is the canonical result model: the
 * interface, the exported project data, the audit record and the report are
 * all rendered from this same message.
 */
export interface CalculationResponse {
  status?: CalculationStatus;
  modules?: ModuleResult[];
  project_quantities?: QuantitySummary;
  versions?: VersionInfo;
  /** Echoed from the request so stale responses can be discarded. */
  request_token?: string;
  /**
   * Hash of the normalized input, used to detect stale results and to key
   * audit records.
   */
  input_hash?: string;
  /**
   * Set when a development-only engineering path produced this result. This
   * can be true even when an underlying ETA component source value is real.
   */
  development_data_used?: boolean;
  messages?: ValidationMessage[];
  assumptions?: Assumption[];
  calculated_at?: string;
  /**
   * Set when the response is a stored demonstration fixture rather than a
   * computed result. This is a stronger statement than development_data_used:
   * nothing here was calculated at all. The interface must present it as such
   * and must never allow it to be mistaken for an engineering result.
   */
  demonstration_result?: boolean;
}

/** A stored, immutable record of one calculation. */
export interface CalculationAudit {
  id?: string;
  project_id?: string;
  project_revision_id?: string;
  input_hash?: string;
  versions?: VersionInfo;
  request?: CalculationRequest;
  response?: CalculationResponse;
  created_at?: string;
  created_by?: string;
  report_artifact_ids?: string[];
}

// ── engineering_rules.proto ─────────────────────────────────────────────

/**
 * Review state of a production engineering-rule package. A package is never
 * usable merely because values exist in it.
 */
export type EngineeringRuleApprovalState =
  | "ENGINEERING_RULE_APPROVAL_STATE_UNSPECIFIED"
  | "ENGINEERING_RULE_APPROVAL_STATE_DRAFT"
  | "ENGINEERING_RULE_APPROVAL_STATE_ENGINEERING_REVIEW"
  | "ENGINEERING_RULE_APPROVAL_STATE_GOLDEN_CASE_VERIFIED"
  | "ENGINEERING_RULE_APPROVAL_STATE_APPROVED"
  | "ENGINEERING_RULE_APPROVAL_STATE_WITHDRAWN";

export const EngineeringRuleApprovalStateValues: readonly EngineeringRuleApprovalState[] = [
  "ENGINEERING_RULE_APPROVAL_STATE_UNSPECIFIED",
  "ENGINEERING_RULE_APPROVAL_STATE_DRAFT",
  "ENGINEERING_RULE_APPROVAL_STATE_ENGINEERING_REVIEW",
  "ENGINEERING_RULE_APPROVAL_STATE_GOLDEN_CASE_VERIFIED",
  "ENGINEERING_RULE_APPROVAL_STATE_APPROVED",
  "ENGINEERING_RULE_APPROVAL_STATE_WITHDRAWN",
] as const;

/** Families of source-backed rules a production wind package can carry. */
export type WindRuleFamily =
  | "WIND_RULE_FAMILY_UNSPECIFIED"
  | "WIND_RULE_FAMILY_EXTERNAL_PRESSURE"
  | "WIND_RULE_FAMILY_INTERNAL_PRESSURE"
  | "WIND_RULE_FAMILY_REFERENCE_HEIGHT"
  | "WIND_RULE_FAMILY_TERRAIN_EXPOSURE"
  | "WIND_RULE_FAMILY_OROGRAPHY"
  | "WIND_RULE_FAMILY_LOAD_CASE";

export const WindRuleFamilyValues: readonly WindRuleFamily[] = [
  "WIND_RULE_FAMILY_UNSPECIFIED",
  "WIND_RULE_FAMILY_EXTERNAL_PRESSURE",
  "WIND_RULE_FAMILY_INTERNAL_PRESSURE",
  "WIND_RULE_FAMILY_REFERENCE_HEIGHT",
  "WIND_RULE_FAMILY_TERRAIN_EXPOSURE",
  "WIND_RULE_FAMILY_OROGRAPHY",
  "WIND_RULE_FAMILY_LOAD_CASE",
] as const;

/** Scope constraints are data rather than hidden assumptions in an engine. */
export interface WindEngineeringScope {
  supported_roof_forms?: string[];
  minimum_roof_slope?: Angle;
  maximum_roof_slope?: Angle;
  required_geometry_conditions?: string[];
  unsupported_conditions?: string[];
}

/**
 * One versioned source-backed rule. Numeric values are optional because a rule
 * package may describe a clause selection or case definition without a single
 * coefficient. Production records must always cite the exact source location.
 */
export interface WindEngineeringRuleRecord {
  id?: string;
  family?: WindRuleFamily;
  rule_kind?: string;
  roof_form?: string;
  zone_id?: string;
  case_id?: string;
  applicability_expression?: string;
  values?: Record<string, Quantity>;
  source_reference?: ReferenceInfo;
}

/** Manifest governing one immutable engineering-rule package. */
export interface WindEngineeringRuleManifest {
  id?: string;
  display_name?: string;
  version?: string;
  design_method_id?: string;
  standard?: string;
  standard_edition?: string;
  national_annex?: string;
  national_annex_edition?: string;
  approval_state?: EngineeringRuleApprovalState;
  scope?: WindEngineeringScope;
  compatible_national_wind_dataset_ids?: string[];
  required_safety_factor_package_id?: string;
  golden_case_set_id?: string;
  approval_reference?: ReferenceInfo;
  source_references?: ReferenceInfo[];
  valid_from?: string;
  valid_until?: string;
  calculation_use_permitted?: boolean;
  blocking_reasons?: string[];
}

/**
 * Complete immutable rule package. Production starts with no package; numeric
 * records are added only after source extraction and engineering approval.
 */
export interface WindEngineeringRulePackage {
  manifest?: WindEngineeringRuleManifest;
  records?: WindEngineeringRuleRecord[];
}

/** Why a selected rule package can or cannot activate a production wind engine. */
export interface WindEngineeringActivationStatus {
  ready?: boolean;
  engineering_rule_package_id?: string;
  national_wind_dataset_id?: string;
  safety_factor_package_id?: string;
  golden_case_set_id?: string;
  messages?: ValidationMessage[];
}

// ── report.proto ────────────────────────────────────────────────────────

/** Progress of an asynchronous report generation. */
export type ReportGenerationState =
  | "REPORT_GENERATION_STATE_UNSPECIFIED"
  | "REPORT_GENERATION_STATE_PENDING"
  | "REPORT_GENERATION_STATE_COMPLETED"
  | "REPORT_GENERATION_STATE_FAILED"
  | "REPORT_GENERATION_STATE_BLOCKED";

export const ReportGenerationStateValues: readonly ReportGenerationState[] = [
  "REPORT_GENERATION_STATE_UNSPECIFIED",
  "REPORT_GENERATION_STATE_PENDING",
  "REPORT_GENERATION_STATE_COMPLETED",
  "REPORT_GENERATION_STATE_FAILED",
  "REPORT_GENERATION_STATE_BLOCKED",
] as const;

/** A selectable report section. */
export interface ReportSection {
  id?: string;
  display_name?: string;
  /** Sections the report cannot omit, for example assumptions and warnings. */
  mandatory?: boolean;
  selected_by_default?: boolean;
}

/** Which sections and drawings a report includes. */
export interface ReportSectionSelection {
  included_section_ids?: string[];
  include_drawings?: boolean;
  /**
   * Preferred drawing scale denominator, for example 100 for 1:100. Zero
   * means the engine fits each drawing to its frame.
   */
  preferred_scale_denominator?: number;
}

/** Metadata printed on the report cover and in the page header. */
export interface ReportMetadata {
  project?: ProjectMetadata;
  report_title?: string;
  report_date?: string;
  decimal_separator?: string;
  show_page_numbers?: boolean;
  additional_notes?: string;
}

/** A request to generate a report from an already-computed result. */
export interface ReportRequest {
  /** The project the report describes. */
  project?: Project;
  /**
   * The authoritative calculation the report renders. The report engine never
   * recalculates: it consumes this result.
   */
  result?: CalculationResponse;
  metadata?: ReportMetadata;
  sections?: ReportSectionSelection;
}

/** A generated report file. */
export interface ReportArtifactInfo {
  id?: string;
  file_name?: string;
  /** MIME type of the artifact, for example "application/pdf". */
  content_type?: string;
  size_bytes?: number;
  page_count?: number;
  /** Hash of the artifact content, used to prove reproducibility. */
  content_hash?: string;
  generated_at?: string;
  versions?: VersionInfo;
}

/** The outcome of a report generation request. */
export interface ReportResponse {
  state?: ReportGenerationState;
  artifact?: ReportArtifactInfo;
  messages?: ValidationMessage[];
}

// ── service.proto ───────────────────────────────────────────────────────

/** Interchange format for an exported drawing. */
export type DrawingExportFormat =
  | "DRAWING_EXPORT_FORMAT_UNSPECIFIED"
  | "DRAWING_EXPORT_FORMAT_DXF";

export const DrawingExportFormatValues: readonly DrawingExportFormat[] = [
  "DRAWING_EXPORT_FORMAT_UNSPECIFIED",
  "DRAWING_EXPORT_FORMAT_DXF",
] as const;

export type GetCapabilitiesRequest = Record<string, never>;

/**
 * What this deployment supports. The interface reads this rather than
 * hard-coding the available methods, countries or unit systems.
 */
export interface GetCapabilitiesResponse {
  versions?: VersionInfo;
  design_methods?: DesignMethod[];
  supported_country_codes?: string[];
  supported_unit_systems?: UnitSystem[];
  report_sections?: ReportSection[];
  /**
   * True when the active catalogue is development-only, so the interface can
   * display the mandatory not-approved-for-design notice.
   */
  development_catalogue_active?: boolean;
  /** Environment name, for example "development" or "production". */
  environment?: string;
}

export interface CreateProjectRequest {
  project?: Project;
}

export interface CreateProjectResponse {
  project?: Project;
}

export interface GetProjectRequest {
  project_id?: string;
}

export interface GetProjectResponse {
  project?: Project;
}

export interface UpdateProjectRequest {
  project?: Project;
}

export interface UpdateProjectResponse {
  project?: Project;
}

export interface ListProjectsRequest {
  page_size?: number;
  page_token?: string;
  include_archived?: boolean;
}

export interface ListProjectsResponse {
  projects?: ProjectSummary[];
  next_page_token?: string;
}

export interface ArchiveProjectRequest {
  project_id?: string;
  archived?: boolean;
}

export interface ArchiveProjectResponse {
  project?: ProjectSummary;
}

export interface ValidateRoofGeometryRequest {
  geometry?: RoofPlanGeometry;
  request_token?: string;
}

export interface ValidateRoofGeometryResponse {
  valid?: boolean;
  geometry?: RoofPlanGeometry;
  messages?: ValidationMessage[];
  request_token?: string;
}

export interface GenerateWindZonesRequest {
  project?: Project;
  module_id?: string;
  request_token?: string;
}

export interface GenerateWindZonesResponse {
  zones?: WindZoneGeometry[];
  messages?: ValidationMessage[];
  request_token?: string;
  /**
   * Backend-derived engineering dimensions for the generated zone layout.
   * Renderers consume these annotations directly and must not recompute the
   * measured distances from the displayed polygons.
   */
  dimensions?: DimensionAnnotation[];
}

export interface ValidateSectionProfileRequest {
  section?: SectionProfile;
}

export interface ValidateSectionProfileResponse {
  valid?: boolean;
  /**
   * The section with its derived values filled in: total insulation thickness
   * and the total fastener length the build-up requires.
   */
  section?: SectionProfile;
  /** Horizontal run of one deck web, derived from the pitch and the flanges. */
  web_run?: Length;
  web_angle?: Angle;
  messages?: ValidationMessage[];
}

export interface GenerateDrawingSceneRequest {
  project?: Project;
  module_id?: string;
  /** Identifier of the scene to build, for example "wind-zone-plan". */
  scene_id?: string;
}

export interface GenerateDrawingSceneResponse {
  scene?: DrawingScene;
  messages?: ValidationMessage[];
}

/**
 * A request to serialise a drawing scene the backend already produced.
 *
 * This converts a file format; it does not author geometry. The scene must be
 * result - because a scene assembled anywhere else is not drawing authority,
 * whatever it looks like once it reaches CAD.
 */
export interface ExportDrawingRequest {
  scene?: DrawingScene;
  format?: DrawingExportFormat;
  /**
   * Base name for the exported file, without an extension. Empty falls back to
   * the scene identifier.
   */
  file_name?: string;
}

export interface ExportDrawingResponse {
  /**
   * The exported document. DXF is a text format, so it is carried inline
   * rather than through a separate artifact download.
   */
  content?: string;
  file_name?: string;
  /** MIME type of the exported document, for example "image/vnd.dxf". */
  content_type?: string;
  size_bytes?: number;
  messages?: ValidationMessage[];
}

export interface ValidateInputRequest {
  project?: Project;
}

export interface ValidateInputResponse {
  valid?: boolean;
  messages?: ValidationMessage[];
}

export interface GetCalculationAuditRequest {
  audit_id?: string;
}

export interface GetCalculationAuditResponse {
  audit?: CalculationAudit;
}

export type ListCatalogueVersionsRequest = Record<string, never>;

export interface ListCatalogueVersionsResponse {
  versions?: CatalogueVersion[];
  active_version_id?: string;
}

/** Selections made so far, used to filter the remaining compatible options. */
export interface CompatibilityContext {
  catalogue_version_id?: string;
  substrate_category?: SubstrateCategory;
  substrate_product_id?: string;
  membrane_product_id?: string;
  total_insulation_thickness?: Length;
  substrate_thickness?: Length;
}

export interface ListCompatibleMembranesRequest {
  context?: CompatibilityContext;
}

export interface ListCompatibleMembranesResponse {
  membranes?: MembraneProduct[];
  messages?: ValidationMessage[];
}

export interface ListCompatibleFixingSystemsRequest {
  context?: CompatibilityContext;
}

export interface ListCompatibleFixingSystemsResponse {
  combinations?: FixingCombination[];
  /**
   * Combinations that were excluded, with the reason, so the interface can
   * explain why an expected option is missing.
   */
  exclusions?: ValidationMessage[];
}

export interface ListAccessoriesRequest {
  context?: CompatibilityContext;
  fixing_combination_id?: string;
}

export interface ListAccessoriesResponse {
  accessories?: AccessoryProduct[];
}

export interface ListSteelDeckProfilesRequest {
  catalogue_version_id?: string;
}

export interface ListSteelDeckProfilesResponse {
  profiles?: SteelDeckProfile[];
}

export interface ValidateCustomProductRequest {
  membrane?: MembraneProduct;
  deck_profile?: SteelDeckProfile;
}

export interface ValidateCustomProductResponse {
  valid?: boolean;
  messages?: ValidationMessage[];
}

/**
 * Returns the validated real ETA-07/0013 component source package as a
 * read-only runtime reference catalogue. The response explicitly retains the
 * calculation-use prohibition of the source package.
 */
export type GetETAReferenceCatalogueRequest = Record<string, never>;

export interface GetETAReferenceCatalogueResponse {
  catalogue?: ETAReferenceCatalogue;
  messages?: ValidationMessage[];
}

/**
 * Lists country dataset manifests without implying that a listed package is
 * active. By default all lifecycle states are returned so review/blocker status
 * can be displayed explicitly.
 */
export interface ListNationalWindDatasetManifestsRequest {
  country_code?: string;
  active_only?: boolean;
}

export interface ListNationalWindDatasetManifestsResponse {
  manifests?: NationalWindDatasetManifest[];
  messages?: ValidationMessage[];
}

/** Returns one exact manifest. It does not return or derive engineering values. */
export interface GetNationalWindDatasetManifestRequest {
  dataset_id?: string;
}

export interface GetNationalWindDatasetManifestResponse {
  manifest?: NationalWindDatasetManifest;
  messages?: ValidationMessage[];
}

/**
 * zone-list selection. The response carries exact dataset values and source
 * references; no country, dataset or wind zone is selected by default.
 */
export interface ListSelectableNationalDatasetsRequest {
  country_code?: string;
}

export interface ListSelectableNationalDatasetsResponse {
  datasets?: NationalDataset[];
  messages?: ValidationMessage[];
}

export interface ListWindEngineeringRulePackagesRequest {
  calculation_ready_only?: boolean;
}

export interface ListWindEngineeringRulePackagesResponse {
  packages?: WindEngineeringRulePackage[];
  messages?: ValidationMessage[];
}

export interface GetWindEngineeringRulePackageRequest {
  package_id?: string;
}

export interface GetWindEngineeringRulePackageResponse {
  package?: WindEngineeringRulePackage;
  messages?: ValidationMessage[];
}

export interface CheckWindEngineeringActivationRequest {
  package_id?: string;
  national_wind_dataset_id?: string;
  safety_factor_package_id?: string;
}

export interface CheckWindEngineeringActivationResponse {
  status?: WindEngineeringActivationStatus;
}

export interface GetReportStatusRequest {
  artifact_id?: string;
}

export interface GetReportStatusResponse {
  state?: ReportGenerationState;
  artifact?: ReportArtifactInfo;
}

// ── Service method paths ────────────────────────────────────────────

/**
 * Method paths follow the Connect-style routing convention
 * POST /<package>.<Service>/<Method> with a JSON body.
 */
export const MethodPaths = {
  PlatformServiceGetCapabilities: "/ejot.roofing.v1.PlatformService/GetCapabilities",
  ProjectServiceCreateProject: "/ejot.roofing.v1.ProjectService/CreateProject",
  ProjectServiceGetProject: "/ejot.roofing.v1.ProjectService/GetProject",
  ProjectServiceUpdateProject: "/ejot.roofing.v1.ProjectService/UpdateProject",
  ProjectServiceListProjects: "/ejot.roofing.v1.ProjectService/ListProjects",
  ProjectServiceArchiveProject: "/ejot.roofing.v1.ProjectService/ArchiveProject",
  GeometryServiceValidateRoofGeometry: "/ejot.roofing.v1.GeometryService/ValidateRoofGeometry",
  GeometryServiceGenerateWindZones: "/ejot.roofing.v1.GeometryService/GenerateWindZones",
  GeometryServiceGenerateDrawingScene: "/ejot.roofing.v1.GeometryService/GenerateDrawingScene",
  GeometryServiceExportDrawing: "/ejot.roofing.v1.GeometryService/ExportDrawing",
  GeometryServiceValidateSectionProfile: "/ejot.roofing.v1.GeometryService/ValidateSectionProfile",
  CalculationServiceValidateInput: "/ejot.roofing.v1.CalculationService/ValidateInput",
  CalculationServiceCalculateModule: "/ejot.roofing.v1.CalculationService/CalculateModule",
  CalculationServiceCalculateProject: "/ejot.roofing.v1.CalculationService/CalculateProject",
  CalculationServiceGetCalculationAudit: "/ejot.roofing.v1.CalculationService/GetCalculationAudit",
  CatalogueServiceListCatalogueVersions: "/ejot.roofing.v1.CatalogueService/ListCatalogueVersions",
  CatalogueServiceListCompatibleMembranes: "/ejot.roofing.v1.CatalogueService/ListCompatibleMembranes",
  CatalogueServiceListCompatibleFixingSystems: "/ejot.roofing.v1.CatalogueService/ListCompatibleFixingSystems",
  CatalogueServiceListAccessories: "/ejot.roofing.v1.CatalogueService/ListAccessories",
  CatalogueServiceListSteelDeckProfiles: "/ejot.roofing.v1.CatalogueService/ListSteelDeckProfiles",
  CatalogueServiceValidateCustomProduct: "/ejot.roofing.v1.CatalogueService/ValidateCustomProduct",
  CatalogueServiceGetETAReferenceCatalogue: "/ejot.roofing.v1.CatalogueService/GetETAReferenceCatalogue",
  WindDataServiceListNationalWindDatasetManifests: "/ejot.roofing.v1.WindDataService/ListNationalWindDatasetManifests",
  WindDataServiceGetNationalWindDatasetManifest: "/ejot.roofing.v1.WindDataService/GetNationalWindDatasetManifest",
  WindDataServiceListSelectableNationalDatasets: "/ejot.roofing.v1.WindDataService/ListSelectableNationalDatasets",
  EngineeringRuleServiceListWindEngineeringRulePackages: "/ejot.roofing.v1.EngineeringRuleService/ListWindEngineeringRulePackages",
  EngineeringRuleServiceGetWindEngineeringRulePackage: "/ejot.roofing.v1.EngineeringRuleService/GetWindEngineeringRulePackage",
  EngineeringRuleServiceCheckWindEngineeringActivation: "/ejot.roofing.v1.EngineeringRuleService/CheckWindEngineeringActivation",
  ReportServiceGenerateReport: "/ejot.roofing.v1.ReportService/GenerateReport",
  ReportServiceGetReportStatus: "/ejot.roofing.v1.ReportService/GetReportStatus",
} as const;

export type MethodPath = (typeof MethodPaths)[keyof typeof MethodPaths];
